import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import {
  getDbConnectionList,
  getProdTableList,
  getProdTableSchema,
  getProdWriteMapping,
  saveProdWriteMapping,
  getNormalizedTableList,
  dryRunProdWrite,
  startProdWrite,
  validateProdMapping,
  type DbConnectionInfo,
  type ProdTableInfo,
  type ProdColumnInfo,
  type ProdColumnBinding,
  type NormalizedTableListItem,
  type DryRunPreviewRow,
  type ValidationIssue,
} from '../../services/data-integration-api';
import ProdColumnBindingEditor from '../../components/data-integration/ProdColumnBindingEditor';
import ProdDictMappingPanel from '../../components/data-integration/ProdDictMappingPanel';
import ProdFkMappingPanel from '../../components/data-integration/ProdFkMappingPanel';

interface ProdWriteConfigProps {
  normalizedTableId: string;
  onBack?: () => void;
}

type Step = 'select' | 'binding' | 'fk' | 'dict' | 'preview';

export default function ProdWriteConfig({ normalizedTableId, onBack }: ProdWriteConfigProps) {
  const navigate = useNavigate();

  // 步骤
  const [step, setStep] = useState<Step>('select');

  // 数据
  const [ntInfo, setNtInfo] = useState<NormalizedTableListItem | null>(null);
  const [prodConnections, setProdConnections] = useState<DbConnectionInfo[]>([]);
  const [selectedConnId, setSelectedConnId] = useState('');
  const [prodTables, setProdTables] = useState<ProdTableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [prodColumns, setProdColumns] = useState<ProdColumnInfo[]>([]);
  const [sourceColumns, setSourceColumns] = useState<any[]>([]);

  // 映射
  const [writeMappingId, setWriteMappingId] = useState<string | null>(null);
  const [bindings, setBindings] = useState<ProdColumnBinding[]>([]);
  const [mappingStatus, setMappingStatus] = useState('');
  const [hasDictFields, setHasDictFields] = useState(false);
  const [hasFkFields, setHasFkFields] = useState(false);

  // 映射校验
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [mappingValid, setMappingValid] = useState<boolean | null>(null);

  // 预览与执行
  const [previewRows, setPreviewRows] = useState<DryRunPreviewRow[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewHasErrors, setPreviewHasErrors] = useState(false);
  const [previewErrorCount, setPreviewErrorCount] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);

  // UI 状态
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 加载规范存取表信息和已有映射
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // 获取规范存取表信息
        const ntResult = await getNormalizedTableList({ creationStatus: 'created' });
        const nt = ntResult.items.find(t => t._id === normalizedTableId);
        if (nt) setNtInfo(nt);

        // 获取生产库连接列表
        const connResult = await getDbConnectionList({ connection_type: 'production' });
        setProdConnections(connResult.items);

        // 检查已有映射
        const existing = await getProdWriteMapping({ normalizedTableId });
        if (existing.mapping) {
          setWriteMappingId(existing.mapping._id);
          setSelectedConnId(existing.mapping.prod_connection_id);
          setSelectedTable(existing.mapping.prod_table_name);
          setMappingStatus(existing.mapping.mapping_status);
          setHasDictFields(existing.mapping.has_dict_fields);
          setHasFkFields(existing.mapping.has_fk_fields || false);
          setBindings(existing.bindings);

          // 加载生产库表列表和结构
          const tables = await getProdTableList({ prodConnectionId: existing.mapping.prod_connection_id });
          setProdTables(tables.tables);
          const schema = await getProdTableSchema({
            prodConnectionId: existing.mapping.prod_connection_id,
            tableName: existing.mapping.prod_table_name,
          });
          setProdColumns(schema.columns);

          // 校验映射有效性
          try {
            const validation = await validateProdMapping(existing.mapping._id);
            setMappingValid(validation.valid);
            setValidationIssues(validation.issues);
            if (!validation.valid) {
              setMappingStatus('invalid');
              toast.warning(`映射校验发现 ${validation.issues.length} 个问题`);
            }
          } catch { /* ignore validation errors */ }

          // 直接进入绑定步骤
          setStep('binding');
        }
      } catch (e: any) {
        toast.error('加载数据失败: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [normalizedTableId]);

  // 加载源表字段（从规范存取表的映射信息）
  useEffect(() => {
    if (!ntInfo) return;
    // 源字段信息从绑定中提取，或者从 generateNormalizedTable 获取
    // 简化处理：使用绑定数据中的源字段信息
    if (bindings.length > 0) {
      const cols: Array<{ column_name: string; data_type: string; ordinal_position: number; native_type: string; character_max_length: number | null; numeric_precision: number | null; numeric_scale: number | null; is_nullable: boolean; column_default: string | null; column_comment: string; is_primary_key: boolean; column_key_type: string | null }> = bindings.map(b => ({
        column_name: b.source_column_name,
        data_type: b.source_data_type,
        ordinal_position: 0,
        native_type: b.source_data_type,
        character_max_length: null,
        numeric_precision: null,
        numeric_scale: null,
        is_nullable: true,
        column_default: null,
        column_comment: '',
        is_primary_key: false,
        column_key_type: null,
      }));
      // 追加 _tenant_code 系统列（供映射到生产表的租户字段）
      if (!cols.some(c => c.column_name === '_tenant_code')) {
        cols.push({
          column_name: '_tenant_code',
          data_type: 'varchar',
          ordinal_position: 0,
          native_type: 'varchar(64)',
          character_max_length: 64,
          numeric_precision: null,
          numeric_scale: null,
          is_nullable: false,
          column_default: null,
          column_comment: '租户编码（同步时填入）',
          is_primary_key: false,
          column_key_type: null,
        });
      }
      setSourceColumns(cols);
    }
  }, [ntInfo, bindings]);

  // 选择生产库连接后加载表列表
  const handleSelectConnection = useCallback(async (connId: string) => {
    setSelectedConnId(connId);
    setSelectedTable('');
    setProdColumns([]);
    if (!connId) return;
    try {
      const result = await getProdTableList({ prodConnectionId: connId });
      setProdTables(result.tables);
    } catch (e: any) {
      toast.error('获取表列表失败: ' + e.message);
    }
  }, []);

  // 选择目标表后加载字段结构
  const handleSelectTable = useCallback(async (tableName: string) => {
    setSelectedTable(tableName);
    if (!tableName || !selectedConnId) return;
    try {
      const schema = await getProdTableSchema({ prodConnectionId: selectedConnId, tableName });
      setProdColumns(schema.columns);
    } catch (e: any) {
      toast.error('获取表结构失败: ' + e.message);
    }
  }, [selectedConnId]);

  // 进入绑定步骤
  const handleEnterBinding = useCallback(async () => {
    if (!selectedConnId || !selectedTable || prodColumns.length === 0) {
      toast.error('请先选择生产库连接和目标表');
      return;
    }

    // 获取源表字段（从规范存取表的目标库映射中获取）
    if (sourceColumns.length === 0 && ntInfo) {
      // 从 getNormalizedTableList 的源表信息中获取
      // 简化：使用 prodColumns 同名匹配作为源字段（实际应从 cdm_column_mapping 获取）
      try {
        const existing = await getProdWriteMapping({ normalizedTableId });
        if (existing.bindings.length > 0) {
          setBindings(existing.bindings);
          const cols: Array<{ column_name: string; data_type: string; ordinal_position: number; native_type: string; character_max_length: number | null; numeric_precision: number | null; numeric_scale: number | null; is_nullable: boolean; column_default: string | null; column_comment: string; is_primary_key: boolean; column_key_type: string | null }> = existing.bindings.map(b => ({
            column_name: b.source_column_name,
            data_type: b.source_data_type,
            ordinal_position: 0, native_type: b.source_data_type,
            character_max_length: null, numeric_precision: null,
            numeric_scale: null, is_nullable: true, column_default: null,
            column_comment: '', is_primary_key: false, column_key_type: null,
          }));
          if (!cols.some(c => c.column_name === '_tenant_code')) {
            cols.push({
              column_name: '_tenant_code', data_type: 'varchar',
              ordinal_position: 0, native_type: 'varchar(64)',
              character_max_length: 64, numeric_precision: null,
              numeric_scale: null, is_nullable: false, column_default: null,
              column_comment: '租户编码（同步时填入）', is_primary_key: false, column_key_type: null,
            });
          }
          setSourceColumns(cols);
        }
      } catch { /* no existing mapping */ }
    }

    setStep('binding');
  }, [selectedConnId, selectedTable, prodColumns, sourceColumns, ntInfo, normalizedTableId]);

  // 保存绑定
  const handleSaveBinding = useCallback(async () => {
    const validBindings = bindings.filter(b => b.prod_column_name);
    if (validBindings.length === 0) {
      toast.error('至少需要绑定一个字段');
      return;
    }

    setSaving(true);
    try {
      const result = await saveProdWriteMapping({
        writeMappingId,
        normalizedTableId,
        prodConnectionId: selectedConnId,
        prodTableName: selectedTable,
        bindings: validBindings,
      });
      setWriteMappingId(result.writeMappingId);
      setMappingStatus(result.mapping_status);
      setHasDictFields(result.has_dict_fields);
      setHasFkFields(result.has_fk_fields);

      const extras: string[] = [];
      if (result.has_fk_fields) extras.push(`${result.fk_field_count} 个 FK 字段`);
      if (result.has_dict_fields) extras.push(`${result.dict_field_count} 个字典字段`);
      toast.success(`保存成功: ${result.binding_count} 个字段绑定${extras.length ? `，${extras.join('、')}待配置` : ''}`);

      if (result.has_fk_fields) {
        setStep('fk');
      } else if (result.has_dict_fields) {
        setStep('dict');
      } else {
        setStep('preview');
      }
    } catch (e: any) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [bindings, writeMappingId, normalizedTableId, selectedConnId, selectedTable]);

  // Dry-run 预览
  const handleDryRun = useCallback(async () => {
    if (!writeMappingId) return;
    setPreviewing(true);
    try {
      const result = await dryRunProdWrite({ writeMappingId, previewRows: 10 });
      setPreviewRows(result.preview_rows);
      setPreviewTotal(result.total_source_rows);
      setPreviewHasErrors(result.has_errors);
      setPreviewErrorCount(result.error_count);
      if (result.has_errors) {
        toast.warning(`预览发现 ${result.error_count} 行有错误，请检查`);
      } else {
        toast.success(`预览成功: ${result.preview_count} 行，总计 ${result.total_source_rows} 条待写入`);
      }
    } catch (e: any) {
      toast.error('预览失败: ' + e.message);
    } finally {
      setPreviewing(false);
    }
  }, [writeMappingId]);

  // 执行写入
  const handleExecute = useCallback(async (writeType?: 'full' | 'incremental') => {
    if (!writeMappingId) return;
    if (!confirm(`确认执行${writeType === 'incremental' ? '增量' : '全量'}写入？总计 ${previewTotal} 条源数据。`)) return;
    setExecuting(true);
    try {
      const result = await startProdWrite({ writeMappingId, writeType });
      toast.success(`写入任务已启动: ${result.total_count} 条记录`);
      navigate(`/data-integration/prod-write-task/${result.writeTaskId}`);
    } catch (e: any) {
      toast.error('启动写入失败: ' + e.message);
      setExecuting(false);
    }
  }, [writeMappingId, previewTotal, navigate]);

  if (loading) {
    return <div className="p-6 text-muted-foreground">加载中...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>← 返回</Button>
          <h2 className="text-base font-semibold">写入生产库配置</h2>
          {ntInfo && <Badge variant="secondary">{ntInfo.normalized_table_name}</Badge>}
          {mappingStatus && (
            <Badge variant={mappingStatus === 'ready' ? 'default' : mappingStatus === 'invalid' ? 'destructive' : 'secondary'}>
              {mappingStatus === 'ready' ? '映射就绪' : mappingStatus === 'invalid' ? '映射失效' : '配置中'}
            </Badge>
          )}
        </div>

        {/* 步骤指示 */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={step === 'select' ? 'font-semibold text-foreground' : ''}>① 选择目标</span>
          <span>→</span>
          <span className={step === 'binding' ? 'font-semibold text-foreground' : ''}>② 字段绑定</span>
          {hasFkFields && <>
            <span>→</span>
            <span className={step === 'fk' ? 'font-semibold text-foreground' : ''}>③ FK映射</span>
          </>}
          {hasDictFields && <>
            <span>→</span>
            <span className={step === 'dict' ? 'font-semibold text-foreground' : ''}>{hasFkFields ? '④' : '③'} 字典对照</span>
          </>}
          <span>→</span>
          <span className={step === 'preview' ? 'font-semibold text-foreground' : ''}>
            {hasFkFields && hasDictFields ? '⑤' : (hasFkFields || hasDictFields) ? '④' : '③'} 预览执行
          </span>
        </div>
      </div>

      <div className="p-6">
        {/* Step 1: 选择生产库连接和目标表 */}
        {step === 'select' && (
          <div className="max-w-xl space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">生产库连接 *</label>
              {prodConnections.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  暂无生产库连接，请先在
                  <Button variant="link" className="px-1 h-auto" onClick={() => navigate('/data-integration/connections')}>连接管理</Button>
                  中添加
                </div>
              ) : (
                <select
                  value={selectedConnId}
                  onChange={e => handleSelectConnection(e.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">-- 请选择 --</option>
                  {prodConnections.map(c => (
                    <option key={c._id} value={c._id}>
                      {c.connection_name} ({c.host}:{c.port}/{c.database_name})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {selectedConnId && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">生产库目标表 *</label>
                {prodTables.length === 0 ? (
                  <p className="text-sm text-muted-foreground">加载中或该库无可用表...</p>
                ) : (
                  <select
                    value={selectedTable}
                    onChange={e => handleSelectTable(e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">-- 请选择 --</option>
                    {prodTables.map(t => (
                      <option key={t.table_name} value={t.table_name}>
                        {t.table_name}{t.table_comment ? ` (${t.table_comment})` : ''} [{t.column_count} 列]
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {selectedTable && prodColumns.length > 0 && (
              <Button onClick={handleEnterBinding}>
                下一步: 字段绑定 →
              </Button>
            )}
          </div>
        )}

        {/* Step 2: 字段绑定 */}
        {step === 'binding' && (
          <div>
            <ProdColumnBindingEditor
              sourceColumns={sourceColumns}
              prodColumns={prodColumns}
              initialBindings={bindings.length > 0 ? bindings : undefined}
              onChange={setBindings}
            />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setStep('select')}>← 上一步</Button>
              <Button onClick={handleSaveBinding} disabled={saving}>
                {saving ? '保存中...' : '保存绑定'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: FK 映射 */}
        {step === 'fk' && writeMappingId && (
          <div>
            <ProdFkMappingPanel
              normalizedTableId={normalizedTableId}
              writeMappingId={writeMappingId}
              prodConnectionId={selectedConnId}
              prodTableName={selectedTable}
              fkFields={bindings
                .filter(b => b.is_fk_field && b.prod_column_name)
                .map(b => ({
                  sourceColumnName: b.source_column_name,
                  prodColumnName: b.prod_column_name,
                }))}
              onComplete={(ms, fcs) => {
                setMappingStatus(ms);
                if (fcs === 'complete') {
                  toast.success('所有 FK 字段配置完成');
                }
              }}
            />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setStep('binding')}>← 返回绑定</Button>
              {hasDictFields ? (
                <Button onClick={() => setStep('dict')}>下一步: 字典对照 →</Button>
              ) : mappingStatus === 'ready' ? (
                <Button onClick={() => setStep('preview')}>下一步: 预览执行 →</Button>
              ) : null}
            </div>
          </div>
        )}

        {/* Step 4: 字典对照 */}
        {step === 'dict' && writeMappingId && (
          <div>
            <ProdDictMappingPanel
              normalizedTableId={normalizedTableId}
              writeMappingId={writeMappingId}
              prodConnectionId={selectedConnId}
              dictFields={bindings
                .filter(b => b.is_dict_field && b.prod_column_name)
                .map(b => ({
                  sourceColumnName: b.source_column_name,
                  prodColumnName: b.prod_column_name,
                }))}
              onComplete={(ms, dcs) => {
                setMappingStatus(ms);
                if (dcs === 'complete') {
                  toast.success('所有字典字段对照配置完成，映射已就绪');
                }
              }}
            />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => hasFkFields ? setStep('fk') : setStep('binding')}>← 上一步</Button>
              {mappingStatus === 'ready' && (
                <Button onClick={() => setStep('preview')}>下一步: 预览执行 →</Button>
              )}
            </div>
          </div>
        )}

        {/* Step 4: 预览执行 */}
        {step === 'preview' && (
          <div className="space-y-4">
            {/* 映射校验警告 */}
            {mappingValid === false && validationIssues.length > 0 && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded space-y-2">
                <p className="text-sm font-semibold text-destructive">映射校验失败 — 请先修复以下问题：</p>
                <ul className="text-xs text-destructive space-y-1 list-disc pl-4">
                  {validationIssues.map((issue, i) => (
                    <li key={i}>{issue.message}</li>
                  ))}
                </ul>
                <Button variant="outline" size="sm" onClick={() => setStep('binding')}>
                  更新字段绑定
                </Button>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={handleDryRun} disabled={previewing || !writeMappingId || mappingValid === false}>
                {previewing ? '预览中...' : '生成预览 (10行)'}
              </Button>
              {previewRows.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  预览 {previewRows.length} 行 / 总计 {previewTotal.toLocaleString()} 条
                  {previewHasErrors && <span className="text-destructive ml-2">{previewErrorCount} 行有错误</span>}
                </span>
              )}
            </div>

            {/* 预览表格 */}
            {previewRows.length > 0 && (
              <div className="border rounded max-h-96 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      {Object.keys(previewRows[0].transformed_row).map(col => (
                        <TableHead key={col} className="text-xs">{col}</TableHead>
                      ))}
                      <TableHead className="text-xs">错误</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((row, i) => (
                      <TableRow key={i} className={row.errors.length > 0 ? 'bg-destructive/5' : ''}>
                        <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                        {Object.values(row.transformed_row).map((val, j) => (
                          <TableCell key={j} className="text-xs font-mono max-w-32 truncate">
                            {val != null ? String(val) : <span className="text-muted-foreground">NULL</span>}
                          </TableCell>
                        ))}
                        <TableCell className="text-xs text-destructive">
                          {row.errors.length > 0 ? row.errors.join('; ') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* 执行按钮 */}
            {previewRows.length > 0 && mappingValid !== false && (
              <div className="flex items-center gap-3 pt-2 border-t">
                <Button
                  onClick={() => handleExecute('full')}
                  disabled={executing}
                >
                  {executing ? '启动中...' : `确认全量写入 (${previewTotal.toLocaleString()} 条)`}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExecute('incremental')}
                  disabled={executing}
                >
                  增量写入 (仅新增)
                </Button>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => {
                if (hasDictFields) setStep('dict');
                else if (hasFkFields) setStep('fk');
                else setStep('binding');
              }}>
                ← 上一步
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
